import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Film, Upload, Instagram, Plus } from 'lucide-react';
import TiktokIcon from '@/components/ui/TiktokIcon';
import UploadModal from '@/components/UploadModal';

const EmptyState = ({ platform, isConnected, onConnect, onUploadSuccess }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);

  const getPlatformDetails = () => {
    switch (platform) {
      case 'tiktok':
        return {
          icon: <TiktokIcon className="h-8 w-8 text-gray-400" />,
          name: 'TikTok',
          connectText: 'Connect TikTok Account',
          emptyText: 'No TikTok videos found'
        };
      case 'instagram':
        return {
          icon: <Instagram className="h-8 w-8 text-gray-400" />,
          name: 'Instagram',
          connectText: 'Connect Instagram Account',
          emptyText: 'No Instagram videos found'
        };
      case 'device':
        return {
          icon: <Upload className="h-8 w-8 text-gray-400" />,
          name: 'Device',
          connectText: 'Upload Videos',
          emptyText: 'No videos uploaded yet',
          uploadText: 'Select videos from your device to get started.'
        };
      default:
        return {
          icon: <Film className="h-8 w-8 text-gray-400" />,
          name: 'Videos',
          connectText: 'Get Started',
          emptyText: 'No videos found'
        };
    }
  };

  const details = getPlatformDetails();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center h-full min-h-[400px] p-4 text-center"
    >
      <div className="bg-gray-800/50 rounded-full p-6 mb-6">
        {details.icon}
      </div>
      <h3 className="text-lg font-medium text-gray-100 mb-2">
        {isConnected ? details.emptyText : `Connect your ${details.name} Account`}
      </h3>
      <p className="text-gray-400 mb-8 max-w-sm">
        {platform === 'device' 
          ? details.uploadText
          : isConnected 
            ? `Start adding content from your ${details.name} account to manage and organize your videos.`
            : `Link your ${details.name} account to import and manage your videos in one place.`}
      </p>
      {platform === 'device' ? (
        <div className="flex flex-col gap-4 w-full max-w-xs">
          <Button
            onClick={() => setShowUploadModal(true)}
            size="lg"
            className="bg-purple-500 hover:bg-purple-600 text-white rounded-full px-8 w-full flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Upload Videos
          </Button>
          <p className="text-xs text-gray-500">
            Supported formats: MP4, MOV, AVI
          </p>
        </div>
      ) : !isConnected && (
        <Button
          onClick={onConnect}
          size="lg"
          className="bg-purple-500 hover:bg-purple-600 text-white rounded-full px-8"
        >
          {details.connectText}
        </Button>
      )}

      <UploadModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        onSuccess={onUploadSuccess}
      />
    </motion.div>
  );
};

export default EmptyState; 